import { FC } from 'react';
import { FileText, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import { Button } from '@new/common/ui';

export interface FileItem {
  id: number;
  name: string;
  url: string;
  size?: number;
  status: 'uploading' | 'completed' | 'failed';
  userProjectId?: number;
}

interface FileCardProps {
  file: FileItem;
  onDownload: (url: string, name: string) => void;
  onRetry?: (id: number) => void;
  clickable?: boolean;
  showStatus?: boolean;
}

const getStatusIcon = (status: string) => {
  if (status === 'uploading')
    return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-(--primary-O900)" />;
  if (status === 'failed') return <AlertCircle size={20} className="text-red-600" />;
  return <CheckCircle2 size={20} className="text-[#47B881]" />;
};

const getCardClassName = (status: string, clickable?: boolean) => {
  const base = 'flex items-center gap-3 p-3 border-2 rounded-lg';
  if (status === 'uploading') return `${base} border-(--primary-O900) bg-(--primary-O50)`;
  if (status === 'failed') return `${base} border-red-200 bg-red-50`;
  return `${base} border-(--neutral-black-NB100) ${clickable ? 'cursor-pointer hover:border-(--primary-O900) transition-colors' : ''}`;
};

const getStatusText = (status: string, size?: number) => {
  if (status === 'uploading') return ' - Uploading...';
  if (status === 'failed') return ' - Failed to upload';
  return size ? ' - Upload complete' : '';
};

export const FileCard: FC<FileCardProps> = ({
  file,
  onDownload,
  onRetry,
  clickable,
  showStatus = true,
}) => {
  return (
    <div
      onClick={() =>
        clickable && file.status === 'completed' && file.url && onDownload(file.url, file.name)
      }
      className={getCardClassName(file.status, clickable)}
    >
      {showStatus && getStatusIcon(file.status)}
      <FileText size={20} className="text-(--primary-O900)" />
      <div className="flex-1">
        <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB900)! m-0!">{file.name}</p>
        <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)! m-0!">
          {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
          {showStatus && getStatusText(file.status, file.size)}
        </p>
      </div>
      {file.status === 'completed' && file.url && !clickable && (
        <button
          onClick={() => onDownload(file.url, file.name)}
          className="p-2 hover:bg-(--neutral-black-NB100) rounded transition-colors"
          title="Download file"
        >
          <Download size={16} className="text-(--primary-O900)" />
        </button>
      )}
      {showStatus && file.status === 'failed' && onRetry && (
        <Button variant="primary" onClick={() => onRetry(file.id)}>
          Upload again
        </Button>
      )}
    </div>
  );
};
